import {
    RequestService,
    RequestServiceResponse,
} from '@tcc-online/request-service';
import IAdminUser from '../types/users/IAdminUser';
import Report from '../types/Report';
import AdminUserProject from '../types/users/AdminUserProject';
import UserTypes from '../../common/constants/UserTypes';
import ImportService from './ImportService';
import { isSuccessfulResponse } from '../../helpers';
import { UserRole } from '../../common/constants/UserRoles';
import { SortOrder } from '../constants/SortOrder';

const BASE_URL = '/users';

const AdminUsersService = {
    getUsers,
    createUser,
    updateUser,
    deleteUser,
    batchDeleteUsers,
    getUser,
    getReports,
    getJobMatchReports,
    getProjects,
    getManagedProjects,
    archive,
    anonymize,
    resetAuth,
    createJobMatch,
    importUsers,
    exportUsers,
    getImportTemplate,
};

const createUrl = (userId?: number) =>
    `${BASE_URL}${!userId ? '' : `/${userId}`}`;

export interface UsersPayload {
    page?: number;
    limit?: number;
    search?: string;
    minRole?: UserRole;
    companyId?: number;
    sortBy?: string;
    sortOrder?: SortOrder;
}

async function getUsers(
    payload?: UsersPayload,
): Promise<RequestServiceResponse<IAdminUser[]>> {
    const {
        page = 1,
        limit = 10,
        search,
        minRole,
        companyId,
        sortBy,
        sortOrder,
    } = payload ?? {};
    return await RequestService.get<IAdminUser[]>(BASE_URL, {
        page,
        limit,
        search,
        minRole,
        companyId,
        sortBy,
        sortOrder,
    });
}

export interface CreatePayload {
    email: string;
    type: typeof UserTypes.User | typeof UserTypes.Admin;
    firstName?: string;
    lastName?: string;
    language?: string;
}

async function createUser(payload: CreatePayload) {
    const { email, type, firstName, lastName, language } = payload;
    return await RequestService.post<CreatePayload>(BASE_URL, {
        email,
        type,
        firstName,
        lastName,
        language,
    });
}

export interface UpdatePayload extends Partial<CreatePayload> {
    userId: number;
}

async function updateUser(payload: UpdatePayload) {
    const { userId, ...fields } = payload;
    const body = Object.fromEntries(
        Object.entries(fields).filter(([, v]) => v !== undefined),
    ) as Partial<Omit<UpdatePayload, 'userId'>>;
    return await RequestService.patch<typeof body, IAdminUser>(
        createUrl(userId),
        body,
        { allowMultiRequest: true },
    );
}

async function deleteUser(
    userId: number,
): Promise<RequestServiceResponse<unknown>> {
    return await RequestService.delete(createUrl(userId));
}

export interface BatchDeletePayload {
    ids: number[];
}

async function batchDeleteUsers(
    payload: BatchDeletePayload,
): Promise<RequestServiceResponse<unknown>> {
    return await RequestService.post<BatchDeletePayload, unknown>(
        `${BASE_URL}/batch-delete`,
        payload,
    );
}

async function getUser(payload: {
    userId?: number;
}): Promise<RequestServiceResponse<IAdminUser>> {
    return await RequestService.get<IAdminUser>(createUrl(payload.userId));
}

async function getReports(payload: { userId: number; limit?: number }) {
    const { userId, limit = 5 } = payload;
    return await RequestService.get<Report[]>(createUrl(userId) + '/reports', {
        limit,
    });
}

async function getJobMatchReports(payload: { userId: number; limit?: number }) {
    const { userId, limit = 5 } = payload;
    return await RequestService.get<Report[]>(
        createUrl(userId) + '/job-matching-reports',
        { limit },
    );
}

async function getProjects(payload: { userId: number; limit?: number }) {
    const { userId, limit = 5 } = payload;
    return await RequestService.get<AdminUserProject[]>(
        createUrl(userId) + '/projects',
        { limit },
    );
}

async function getManagedProjects(payload: { userId: number; limit?: number }) {
    const { userId, limit = 5 } = payload;
    return await RequestService.get<AdminUserProject[]>(
        createUrl(userId) + '/managed-projects',
        { limit },
    );
}

async function archive(userId: number): Promise<RequestServiceResponse> {
    return await RequestService.post<IAdminUser>(
        createUrl(userId) + '/archive',
    );
}

async function anonymize(userId: number): Promise<RequestServiceResponse> {
    return await RequestService.post<IAdminUser>(
        createUrl(userId) + '/anonymize',
    );
}

async function resetAuth(userId: number): Promise<RequestServiceResponse> {
    return await RequestService.post<IAdminUser>(
        createUrl(userId) + '/reset-credentials',
    );
}

async function createJobMatch(userId: number): Promise<RequestServiceResponse> {
    return await RequestService.post<IAdminUser>(
        `/admin/job-matchings/from-user/${userId}`,
    );
}

async function importUsers(payload: { companyId: number; data: FormData }) {
    const { companyId, data } = payload;
    const result = await RequestService.post<FormData, { importId: number }>(
        `/admin/companies/${companyId}/users/import`,
        data,
        {
            headers: { 'Content-Type': 'multipart/form-data' },
        },
    );

    if (isSuccessfulResponse(result.status)) {
        ImportService.saveImportId(result.data.importId);
    }

    return result;
}

async function getImportTemplate(companyId: number) {
    return await RequestService.get<Blob>(
        `/admin/companies/${companyId}/users/import/template`,
        {},
        { responseType: 'blob' },
    );
}

async function exportUsers(companyId: number) {
    return await RequestService.get<Blob>(
        `/admin/companies/${companyId}/users/export`,
        {},
        { responseType: 'blob' },
    );
}

export default AdminUsersService;
